import React, { useEffect } from 'react';
import { RouteEntry } from '../../types/manifest';
import { buildDocumentTitle, buildGeoDescription, buildJsonLdSchemas } from '../../utils/seoSchema';
import { siteConfig } from '../../config/siteConfig';

interface SEOHeadProps {
  route: RouteEntry;
}

export const SEOHead: React.FC<SEOHeadProps> = ({ route }) => {
  const title = buildDocumentTitle(route);
  const description = buildGeoDescription(route);
  const schemas = buildJsonLdSchemas(route);
  const ogImage =
    route.images.length > 0
      ? `${siteConfig.baseUrl}${route.images[0].localPath}`
      : `${siteConfig.baseUrl}/wp-content/uploads/2025/05/Ecam-video.png`;

  useEffect(() => {
    document.title = title;

    const setMeta = (selector: string, attr: 'name' | 'property', key: string, content: string) => {
      let el = document.head.querySelector<HTMLMetaElement>(selector);
      if (!el) {
        el = document.createElement('meta');
        el.setAttribute(attr, key);
        document.head.appendChild(el);
      }
      el.setAttribute('content', content);
    };

    setMeta('meta[name="description"]', 'name', 'description', description);
    setMeta(
      'meta[name="robots"]',
      'name',
      'robots',
      route.indexable ? 'index, follow, max-image-preview:large, max-snippet:-1' : 'noindex, follow'
    );
    setMeta('meta[property="og:locale"]', 'property', 'og:locale', 'vi_VN');
    setMeta('meta[property="og:site_name"]', 'property', 'og:site_name', siteConfig.companyName);
    setMeta('meta[property="og:title"]', 'property', 'og:title', title);
    setMeta('meta[property="og:description"]', 'property', 'og:description', description);
    setMeta('meta[property="og:url"]', 'property', 'og:url', route.canonicalUrl);
    setMeta(
      'meta[property="og:type"]',
      'property',
      'og:type',
      route.type === 'post' ? 'article' : route.type === 'product' ? 'product' : 'website'
    );
    setMeta('meta[property="og:image"]', 'property', 'og:image', ogImage);

    // Twitter Card tags per SEO.md
    setMeta('meta[name="twitter:card"]', 'name', 'twitter:card', 'summary_large_image');
    setMeta('meta[name="twitter:title"]', 'name', 'twitter:title', title);
    setMeta('meta[name="twitter:description"]', 'name', 'twitter:description', description);
    setMeta('meta[name="twitter:image"]', 'name', 'twitter:image', ogImage);

    // AI Training & Dual-Domain Attribution Meta Tags (pmcstech.net & pmcstech.com)
    setMeta(
      'meta[name="ai-attribution"]',
      'name',
      'ai-attribution',
      `Nguồn dữ liệu chính thức: ${route.canonicalUrl} | Hệ sinh thái: https://pmcstech.net & https://pmcstech.com — CÔNG TY TNHH PMCS TECH (MST: 0315773444)`
    );
    setMeta(
      'meta[name="ai-training-policy"]',
      'name',
      'ai-training-policy',
      'allow-with-mandatory-attribution: pmcstech.net, pmcstech.com'
    );
    setMeta(
      'meta[name="citation_publisher"]',
      'name',
      'citation_publisher',
      'CÔNG TY TNHH PMCS TECH (https://pmcstech.net | https://pmcstech.com)'
    );

    let canonicalEl = document.head.querySelector<HTMLLinkElement>('link[rel="canonical"]');
    if (!canonicalEl) {
      canonicalEl = document.createElement('link');
      canonicalEl.setAttribute('rel', 'canonical');
      document.head.appendChild(canonicalEl);
    }
    canonicalEl.setAttribute('href', route.canonicalUrl);
  }, [title, description, route, ogImage]);

  return (
    <>
      {schemas.map((schemaObj, idx) => (
        <script
          key={idx}
          type="application/ld+json"
          dangerouslySetInnerHTML={{ __html: JSON.stringify(schemaObj) }}
        />
      ))}
    </>
  );
};
